// web/src/screens/mic_beep_pref.ts — per-device override for the mic-open
// beep that plays right before MediaRecorder.start() on each card turn.
//
// Resolution order at turn time (card.ts):
//   1. localStorage override ("on"|"off") if the user has flipped the toggle
//      on this device from the /config screen.
//   2. Operator default from GET /api/config/mic-beep, fetched once per
//      card-screen mount and cached in-memory for the rest of that mount.
//   3. False (no beep) if the fetch fails — the beep is non-critical.
//
// Mirrors final.ts's localStorage-only preference pattern; the override
// stays per-device so a parent toggling it on a tablet doesn't follow
// them to a different device.
import { getMicBeepDefault } from "../api/config.js";

const STORAGE_KEY = "micOpenBeep";

export type MicBeepOverride = "on" | "off" | null;

export function readMicBeepOverride(): MicBeepOverride {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === "on" || raw === "off") return raw;
    return null;
  } catch {
    return null;
  }
}

export function writeMicBeepOverride(v: MicBeepOverride): void {
  try {
    if (v === null) localStorage.removeItem(STORAGE_KEY);
    else localStorage.setItem(STORAGE_KEY, v);
  } catch { /* localStorage unavailable — best-effort */ }
}

// Resolves the effective preference: localStorage override wins; otherwise
// fetches the operator default; on any failure, returns false (no beep).
export async function resolveMicBeepEnabled(): Promise<boolean> {
  const override = readMicBeepOverride();
  if (override !== null) return override === "on";
  try {
    const cfg = await getMicBeepDefault();
    return cfg.mic_open_beep_default;
  } catch {
    return false;
  }
}
